import { Pencil, FileText, Calendar, CalendarClock } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { getPleaTypeStyle, getPleaStatusStyle, isPleaDecided } from '../constants';
import { AttachmentList } from './AttachmentList';
import type { Attachment, Plea } from '../types';

interface PleaDetailDialogProps {
  plea: Plea | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (plea: Plea) => void;
  attachments: Attachment[];
  attachmentsLoading: boolean;
  attachmentsError: string | null;
}

interface DetailRowProps {
  icon: React.ElementType;
  label: string;
  value: string;
}

function DetailRow({ icon: Icon, label, value }: DetailRowProps) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 flex-none text-slate-400" aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</p>
        <p className="text-sm font-semibold text-slate-900">{value}</p>
      </div>
    </div>
  );
}

function formatDateTimeLabel(dateStr: string): string {
  try {
    return format(parseISO(dateStr), "d 'de' MMM. 'de' yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return dateStr;
  }
}

export function PleaDetailDialog({
  plea,
  open,
  onOpenChange,
  onEdit,
  attachments,
  attachmentsLoading,
  attachmentsError,
}: PleaDetailDialogProps) {
  if (!plea) return null;

  const typeStyle = getPleaTypeStyle(plea.type);
  const statusStyle = getPleaStatusStyle(plea.status);
  const TypeIcon = typeStyle.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[90dvh] max-w-sm flex-col overflow-y-auto"
        aria-label="Detalhes do requerimento"
      >
        <DialogHeader className="text-left">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border',
                typeStyle.bg,
                typeStyle.border,
              )}
            >
              <TypeIcon className={cn('h-5 w-5', typeStyle.color)} aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="truncate">{plea.type}</DialogTitle>
              <span
                className={cn(
                  'mt-1 inline-block rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                  statusStyle.bg,
                  statusStyle.text,
                )}
              >
                {statusStyle.label}
              </span>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="space-y-2.5 rounded-xl bg-slate-50 p-3">
            <DetailRow icon={FileText} label="Descrição" value={plea.description} />
            <DetailRow icon={Calendar} label="Início" value={formatDateTimeLabel(plea.start)} />
            {plea.end && <DetailRow icon={CalendarClock} label="Fim" value={formatDateTimeLabel(plea.end)} />}
          </div>

          <Separator />

          <div>
            <p className="mb-1 text-xs font-medium text-slate-500">Anexos enviados</p>
            <AttachmentList
              attachments={attachments}
              attachmentsLoading={attachmentsLoading}
              attachmentsError={attachmentsError}
            />
          </div>
        </div>

        {!isPleaDecided(plea.status) && (
          <div className="pt-2">
            <Button className="w-full gap-2" onClick={() => onEdit(plea)}>
              <Pencil className="h-4 w-4" aria-hidden="true" />
              Editar pedido
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
